import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { Finding } from "../../gait/bands";
import { ScoreRing } from "../components/ScoreRing";
import { KinematicCurves } from "../components/KinematicCurves";
import { PhaseStrip } from "../components/PhaseStrip";
import { LegConfidence } from "../components/LegConfidence";
import {
  getSession,
  getStream,
  getAnalysis,
  getVideoBlob,
  getPatient,
  deleteSession,
  listSessions,
} from "../../store/db";
import type { AnalysisResult, Patient, Session } from "../../store/types";
import type { LandmarkStream } from "../../store/types";
import { drawSkeleton, eventsToMarkers, frameIndexAtMs } from "../../viz/overlay";
import { interpret } from "../../gait/interpret";
import { referenceRange, evaluateNorm } from "../../gait/norms";
import type { SidePair } from "../../gait/clinical";
import { buildReportHtml, shareOrPrintReport } from "../../report/report";
import { buildStillPng, buildMontagePng, downloadBlob } from "../../export/montage";
import { PILOT } from "../../config";

export function ReviewScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  const [session, setSession] = useState<Session | null>(null);
  const [stream, setStream] = useState<LandmarkStream | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [prior, setPrior] = useState<{ overall: number; recordedAt: number } | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let url: string | null = null;
    (async () => {
      const [s, st, a, blob] = await Promise.all([
        getSession(sessionId),
        getStream(sessionId),
        getAnalysis(sessionId),
        getVideoBlob(sessionId),
      ]);
      setSession(s ?? null);
      setStream(st ?? null);
      setAnalysis(a ?? null);
      if (s) setPatient((await getPatient(s.patientId)) ?? null);
      if (blob) {
        url = URL.createObjectURL(blob);
        setVideoUrl(url);
      }
    })();
    return () => {
      if (url) URL.revokeObjectURL(url);
      cancelAnimationFrame(rafRef.current);
    };
  }, [sessionId]);

  // Trend: find the most recent earlier session for the same person + view and
  // compare its overall score (longitudinal context, computed locally).
  useEffect(() => {
    if (!session || !analysis?.scores) {
      setPrior(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const all = await listSessions();
      const priors = all
        .filter(
          (s) =>
            s.patientId === session.patientId &&
            s.view === session.view &&
            s.id !== session.id &&
            s.recordedAt < session.recordedAt &&
            // Only compare across compatible algorithm versions.
            s.versions.feature === session.versions.feature &&
            s.versions.scoring === session.versions.scoring,
        )
        .sort((a, b) => b.recordedAt - a.recordedAt);
      for (const p of priors) {
        const a = await getAnalysis(p.id);
        if (a?.scores) {
          if (!cancelled) setPrior({ overall: a.scores.overall, recordedAt: p.recordedAt });
          return;
        }
      }
      if (!cancelled) setPrior(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, analysis]);

  const renderFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !stream) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (canvas.width !== video.videoWidth) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    if (!showOverlay) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    const idx = frameIndexAtMs(stream.frames, video.currentTime * 1000);
    const frame = stream.frames[idx];
    if (frame) drawSkeleton(ctx, frame, canvas.width, canvas.height);
  }, [stream, showOverlay]);

  useEffect(() => {
    const loop = () => {
      renderFrame();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [renderFrame]);

  async function onDelete() {
    if (!sessionId) return;
    if (!confirm("Delete this session, its video and all data from this device?")) return;
    await deleteSession(sessionId);
    nav("/");
  }

  if (!session) return <div className="card">Loading…</div>;

  const clinical = analysis?.clinical;
  const rom = analysis?.rom;
  const interp =
    analysis?.scores
      ? interpret({
          view: session.view,
          scores: analysis.scores,
          findings: analysis.findings ?? [],
          temporal: analysis.temporal,
          clinical: analysis.clinical,
          heightCm: patient?.heightCm,
        })
      : null;
  const markers =
    analysis && session.durationMs
      ? eventsToMarkers(analysis.events, session.durationMs)
      : [];

  function downloadReport() {
    if (!analysis || !session) return;
    void shareOrPrintReport(buildReportHtml(session, patient, analysis), fileBase(session));
  }

  function fileBase(s: Session): string {
    return `${(patient?.code || "gait").replace(/\s+/g, "_")}_${new Date(s.recordedAt).toISOString().slice(0, 10)}`;
  }
  function metaFor(s: Session) {
    return { label: patient?.code || "—", sub: `${s.view} · ${new Date(s.recordedAt).toLocaleDateString()}` };
  }

  async function exportStill() {
    if (!videoUrl || !session) return;
    setExporting(true);
    try {
      const tMs = (videoRef.current?.currentTime ?? 0) * 1000;
      const blob = await buildStillPng({ videoUrl, tMs, frames: stream?.frames, overlay: showOverlay, meta: metaFor(session) });
      downloadBlob(blob, `${fileBase(session)}_still.png`);
    } finally {
      setExporting(false);
    }
  }

  async function exportMontage() {
    if (!videoUrl || !session || !stream) return;
    setExporting(true);
    try {
      const startMs = analysis?.boutMs?.startMs ?? 0;
      const endMs = analysis?.boutMs?.endMs ?? session.durationMs;
      const blob = await buildMontagePng({ videoUrl, frames: stream.frames, startMs, endMs, overlay: true, meta: metaFor(session) });
      downloadBlob(blob, `${fileBase(session)}_montage.png`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="review-actions">
        <div className="review-actions-meta">
          <a href={`#/trend/${session.patientId}`} style={{ color: "inherit" }}>
            <strong>{patient?.code || "Analyse"}</strong>
          </a>
          <span className="muted">
            {session.view === "frontal" ? "frontal" : "seitlich"} ·{" "}
            {new Date(session.recordedAt).toLocaleDateString()}
          </span>
        </div>
        <button className="btn" style={{ width: "auto", padding: "11px 18px" }} onClick={downloadReport}>
          Bericht (PDF)
        </button>
        <button
          className="btn secondary"
          style={{ width: "auto", padding: "11px 16px" }}
          onClick={() => nav("/")}
        >
          Wiederholen
        </button>
      </div>

      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <strong>
            {session.view === "frontal" ? "Frontal" : "Lateral"} · {session.milestone}
          </strong>
          <span className="muted">{new Date(session.recordedAt).toLocaleString()}</span>
        </div>

        <div className="video-wrap" style={{ marginTop: 12 }}>
          {videoUrl && (
            <video ref={videoRef} src={videoUrl} controls playsInline muted />
          )}
          <canvas ref={canvasRef} />
        </div>

        <div className="markers" style={{ marginTop: 10 }}>
          {markers.map((m, i) => (
            <div
              key={i}
              className={`marker ${m.type} ${m.side}`}
              style={{ left: `${m.fraction * 100}%` }}
              title={`${m.side} ${m.type}`}
            />
          ))}
        </div>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="muted">
            ▮ heel-strike · ▪ toe-off · <span style={{ color: "var(--accent)" }}>L</span> /{" "}
            <span style={{ color: "var(--accent2)" }}>R</span>
          </span>
          <button className="btn secondary" style={{ width: "auto", padding: "6px 12px" }} onClick={() => setShowOverlay((v) => !v)}>
            {showOverlay ? "Hide skeleton" : "Show skeleton"}
          </button>
        </div>
      </div>

      {!session.qc.passed && (
        <div className="card">
          <div className="warnbox">
            <div className="t">Capture quality warning</div>
            <p className="muted" style={{ margin: "6px 0 0" }}>
              Results may be unreliable. Full-body {Math.round(session.qc.fullBodyRatio * 100)}% ·
              confidence {Math.round(session.qc.meanConfidence * 100)}% · {session.qc.fps.toFixed(0)} fps ·
              {" "}{session.qc.detectedCycles} cycles.
            </p>
            {session.qc.warnings && session.qc.warnings.length > 0 && (
              <ul className="checklist" style={{ marginTop: 8 }}>
                {session.qc.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="btn secondary"
              style={{ marginTop: 10, width: "auto", padding: "6px 12px" }}
              onClick={() => nav("/")}
            >
              Neu aufnehmen
            </button>
          </div>
        </div>
      )}

      {analysis?.viewAdvisory && (
        <div className="card">
          <div className="warnbox">
            <div className="t">View check</div>
            <p className="muted" style={{ margin: "6px 0 0" }}>{analysis.viewAdvisory}</p>
          </div>
        </div>
      )}

      {analysis?.scores && (
        <div className="card">
          <div className="score-hero">
            <ScoreRing value={analysis.scores.overall} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Overall Gait Score</div>
              <div className="muted" style={{ marginTop: 4 }}>
                {session.view} view · {session.qc.detectedCycles} cycles
                {analysis.boutMs &&
                  ` · analysed ${(analysis.boutMs.startMs / 1000).toFixed(1)}–${(analysis.boutMs.endMs / 1000).toFixed(1)}s`}
              </div>
              <div className="row" style={{ marginTop: 8, gap: 6, flexWrap: "wrap" }}>
                <span className="pill">confidence: {analysis.scores.confidence}</span>
                {prior && <TrendChip now={analysis.scores.overall} prior={prior} />}
                {prior && (
                  <a href={`#/trend/${session.patientId}`} className="pill" style={{ fontWeight: 600 }}>
                    Verlauf ansehen ›
                  </a>
                )}
                {PILOT.evaluation && (
                  <span className="pill" style={{ color: "var(--warn)", background: "rgba(255,159,10,0.14)" }}>
                    evaluation
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="subscores">
            <Subscore k="Symmetry" v={analysis.scores.symmetry} />
            <Subscore k="Pelvic stability" v={analysis.scores.pelvicStability} />
            <Subscore k="Knee control" v={analysis.scores.kneeControl} />
            <Subscore k="Trunk stability" v={analysis.scores.trunkStability} />
            <Subscore k="Gait consistency" v={analysis.scores.consistency} />
          </div>
        </div>
      )}

      {analysis?.legProximity && analysis.findings && (
        <LegConfidence proximity={analysis.legProximity} findings={analysis.findings} />
      )}

      {rom && <KinematicCurves rom={rom} />}
      {rom && <PhaseStrip />}

      {analysis?.findings && analysis.findings.length > 0 && (
        <div className="card">
          <strong>Findings</strong>
          <div style={{ marginTop: 8 }}>
            {analysis.findings.map((f) => (
              <div className="finding" key={f.key}>
                <div className="lbl">
                  {f.label}
                  <small>
                    {f.scale === "tendency" ? "tendency" : "deviation"} · confidence{" "}
                    {f.confidence}
                  </small>
                </div>
                <span className={`chip ${bandClass(f.band)}${f.confidence === "low" ? " lowconf" : ""}`}>
                  {f.band}
                </span>
              </div>
            ))}
          </div>
          <p className="muted" style={{ marginTop: 10 }}>
            Categorical bands only — no clinical joint angles. Seed thresholds
            ({session.versions.threshold}); calibrate before any clinical claim.
          </p>
        </div>
      )}

      {interp && (
        <div className="card">
          <strong>Interpretation</strong>
          <p style={{ marginTop: 10 }}>{interp.summary}</p>
          <p className="muted" style={{ marginTop: 6 }}>
            <b style={{ color: "var(--text)" }}>Asymmetry:</b> {interp.asymmetry}
          </p>
          {interp.keyFindings.length > 0 && (
            <ul style={{ margin: "8px 0", paddingLeft: 18 }}>
              {interp.keyFindings.map((k, i) => (
                <li key={i} style={{ fontSize: 14 }}>{k}</li>
              ))}
            </ul>
          )}
          {interp.flags.length > 0 && (
            <div className="warnbox" style={{ marginTop: 8 }}>
              <div className="t">Flags</div>
              <ul>
                {interp.flags.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {clinical && (
        <div className="card">
          <strong>Gait metrics</strong>
          <div style={{ marginTop: 8 }}>
            <ClinRow label="Cadence" value={clinical.cadenceStepsPerMin} unit="steps/min" normKey="cadenceStepsPerMin" digits={0} height={patient?.heightCm} />
            {clinical.speedKmh !== null && (
              <ClinRow label="Speed (est.)" value={clinical.speedKmh} unit="km/h" normKey="speedKmh" digits={1} height={patient?.heightCm} />
            )}
            <PairRow label="Stance time L/R" pair={clinical.stanceTimePct} unit="%" />
            <PairRow label="Double support L/R" pair={clinical.doubleSupportPct} unit="%" />
            <PairRow label="Step time L/R" pair={clinical.stepTimeS} unit="s" digits={2} />
            {(clinical.stepLengthCm.L !== null || clinical.stepLengthCm.R !== null) && (
              <PairRow label="Step length L/R (est.)" pair={clinical.stepLengthCm} unit="cm" />
            )}
            <PairRow label="Gait variability L/R" pair={clinical.gaitVariabilityPct} unit="%" />
          </div>
          {clinical.spatialConfidence !== "none" && (
            <p className="muted" style={{ marginTop: 8 }}>
              Spatial values estimated from height ({clinical.spatialConfidence} confidence, ±10–15%).
            </p>
          )}
        </div>
      )}

      {rom && (
        <div className="card">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <strong>Range of motion (estimated)</strong>
            <span className="muted">{rom.confidence} confidence</span>
          </div>
          <div style={{ marginTop: 8 }}>
            <RomRow label="Hip" l={rom.hip.L} r={rom.hip.R} />
            <RomRow label="Knee" l={rom.knee.L} r={rom.knee.R} />
            <RomRow label="Ankle (rel.)" l={rom.ankle.L} r={rom.ankle.R} />
          </div>
          <p className="muted" style={{ marginTop: 8 }}>
            Sagittal ROM estimated from a single camera — for movement-range context,
            not diagnostic angle measurement.
          </p>
        </div>
      )}


      <div className="card">
        <strong>Export</strong>
        <div className="row" style={{ gap: 10, marginTop: 12 }}>
          <button className="btn secondary" onClick={exportStill} disabled={exporting || !videoUrl}>
            {exporting ? "…" : "Still image"}
          </button>
          <button className="btn secondary" onClick={exportMontage} disabled={exporting || !videoUrl}>
            {exporting ? "Rendering…" : "Time-lapse image"}
          </button>
        </div>
        <p className="muted" style={{ marginTop: 12 }}>
          Still = current video frame (with skeleton if shown). Time-lapse =
          stroboscopic composite of the analysed walk. Full report is at the top.
        </p>
        <div className="row" style={{ gap: 10, marginTop: 12 }}>
          <button className="btn secondary" onClick={() => nav("/")}>
            New capture
          </button>
          <button className="btn danger" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>

      <p className="disclaimer">
        Functional screening only — not a motion-lab replacement or diagnostic
        device. Sagittal angles are estimated from a single camera for ROM
        context. Pose {session.versions.poseModel} · features {session.versions.feature}
        {" · "}thresholds {session.versions.threshold}.
      </p>
    </>
  );
}

function scoreColor(score: number): string {
  if (score >= 80) return "#5eead4";
  if (score >= 60) return "#7cc4ff";
  if (score >= 40) return "#ffd60a";
  return "#ff6b6b";
}

function bandClass(band: Finding["band"]): string {
  switch (band) {
    case "NORMAL":
    case "LOW":
      return "b0";
    case "MILD DEVIATION":
      return "b1";
    case "MODERATE DEVIATION":
    case "MODERATE":
      return "b2";
    case "SEVERE DEVIATION":
    case "HIGH":
      return "b3";
    default:
      return "b0";
  }
}

function ClinRow({
  label,
  value,
  unit,
  normKey,
  digits = 1,
  height,
}: {
  label: string;
  value: number | null;
  unit: string;
  normKey: string;
  digits?: number;
  height?: number;
}) {
  const range = referenceRange(normKey, { heightCm: height });
  let status = "";
  let color = "var(--muted)";
  if (value !== null && range) {
    const ev = evaluateNorm(value, range);
    if (ev.severity === "NORMAL") {
      status = "in range";
      color = "var(--accent)";
    } else {
      status = `${ev.position} · ${ev.severity.toLowerCase()}`;
      color = ev.severity === "MILD" ? "#a3e635" : ev.severity === "MODERATE" ? "#fbbf24" : "#f87171";
    }
  }
  return (
    <div className="finding">
      <div className="lbl">
        {label}
        {range && (
          <small>
            ref {range.low.toFixed(0)}–{range.high.toFixed(0)} {range.unit}
          </small>
        )}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 600 }}>
          {value === null ? "—" : value.toFixed(digits)} {unit}
        </div>
        {status && <small style={{ color }}>{status}</small>}
      </div>
    </div>
  );
}

function PairRow({
  label,
  pair,
  unit,
  digits = 0,
}: {
  label: string;
  pair: SidePair;
  unit: string;
  digits?: number;
}) {
  const f = (x: number | null) => (x === null ? "—" : x.toFixed(digits));
  return (
    <div className="finding">
      <div className="lbl">{label}</div>
      <div style={{ fontWeight: 600 }}>
        {f(pair.L)} / {f(pair.R)} {unit}
      </div>
    </div>
  );
}

function RomRow({
  label,
  l,
  r,
}: {
  label: string;
  l: { min: number; max: number; excursion: number };
  r: { min: number; max: number; excursion: number };
}) {
  return (
    <div className="finding">
      <div className="lbl">
        {label}
        <small>
          L {l.min.toFixed(0)}…{l.max.toFixed(0)}° · R {r.min.toFixed(0)}…{r.max.toFixed(0)}°
        </small>
      </div>
      <div style={{ fontWeight: 600, textAlign: "right" }}>
        {l.excursion.toFixed(0)}° / {r.excursion.toFixed(0)}°
        <small style={{ display: "block", color: "var(--muted)" }}>ROM L / R</small>
      </div>
    </div>
  );
}

function TrendChip({
  now,
  prior,
}: {
  now: number;
  prior: { overall: number; recordedAt: number };
}) {
  const delta = now - prior.overall;
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "±";
  const when = new Date(prior.recordedAt).toLocaleDateString();
  return (
    <span className={`pill trend ${dir}`} title={`Vorher ${prior.overall} am ${when}`}>
      {arrow} {delta > 0 ? "+" : ""}
      {delta} vs. letzte Messung
    </span>
  );
}

function Subscore({ k, v }: { k: string; v?: number }) {
  const has = v !== undefined;
  return (
    <div className="subscore">
      <div className="k">{k}</div>
      <div className="bar">
        <div
          style={{
            width: `${has ? v : 0}%`,
            background: has ? scoreColor(v as number) : "transparent",
          }}
        />
      </div>
      <div className="v">{has ? v : "n/a"}</div>
    </div>
  );
}
